import React, { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { twJoin, twMerge } from 'tailwind-merge';

import { ArchiveIcon, EditIcon, RefreshIcon, ThreeDotsIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { DeprecatedModalWrapper } from '../../../components/modal/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/popover/index';
import {
  AnalyticHooks,
  Company as CompanyHook,
  useCompanyStorageDisplayUrl,
  useFormat,
  useLocale,
  useModalState,
} from '../../../hooks/index';
import { companyTypeTranslation } from '../../../translations/company-translation';
import { stakeholderRoleTranslation } from '../../../translations/stakeholder-translation.util';
import { Company } from '../../../types/company.types';
import { PossibleAction } from '../../../types/google-analytics-types.types';
import { PageRoute } from '../../../types/page.types';
import { StakeholderRole } from '../../../types/stakeholder.types';
import CompanyCardItem from './company-card-item';
import CompanyDeactivateModal from './company-deactivate-modal';
import IncompleteCompanyCard from './incomplete-company-card';

type MyCompanyCardProps = {
  invalidateQuery: () => void;
} & Company;

const MyCompanyCard: FC<MyCompanyCardProps> = (company) => {
  const {
    invalidateQuery,
    createdAt,
    isActive,
    isCompleted,
    logoImgSrc,
    name,
    isExternalGuest,
    roleInCompany,
    type,
    isSelected,
    id,
    toCancelAt,
    inactivateAt,
    isEnterpriseRequested,
  } = company;
  const logoDisplaySrc = useCompanyStorageDisplayUrl(id, logoImgSrc);
  const { format } = useFormat();
  const navigate = useNavigate();
  const { messagesLocale } = useLocale();

  const createdAtFormattedData = useMemo(
    () => format(createdAt, 'dd/MM/yyyy'),
    [createdAt, format],
  );

  const toCancelAtFormattedData = useMemo(
    () => (toCancelAt ? format(toCancelAt, 'dd/MM/yyyy') : undefined),
    [format, toCancelAt],
  );

  const inactivateAtFormattedData = useMemo(
    () => (inactivateAt ? format(inactivateAt, 'dd/MM/yyyy') : undefined),
    [format, inactivateAt],
  );

  const {
    isOpen: isOpenDeactivateModal,
    handleOpenModal,
    handleSuccessModal,
    handleDismissModal,
  } = useModalState({
    onSuccess: () => {
      deactivate({ companyId: id }, { onSuccess: invalidateQuery });
    },
  });

  const { paymentData } = CompanyHook.usePaymentDetails({
    companyId: id,
    enabled: isOpenDeactivateModal,
  });

  const { deactivate } = CompanyHook.useDeactivate();
  const companyReactivateTracker = AnalyticHooks.useClick(PossibleAction.REACTIVATE_COMPANY);
  const { setSelected } = CompanyHook.useSetSelected();
  const { reactivate, isPending: isReactivatePending } = CompanyHook.useReactivate();

  const handleSelectCompany = useCallback(() => {
    return new Promise<void>((resolve) => {
      setSelected(id, {
        onSuccess: () => {
          invalidateQuery();
          resolve();
        },
      });
    });
  }, [id, invalidateQuery, setSelected]);

  const handleSelectEnterpiseRequestCompany = useCallback(
    () => navigate(PageRoute.HANG_TIGHT),
    [navigate],
  );

  const handleReactivateCompany = useCallback(() => {
    if (isReactivatePending) return;
    reactivate(
      { companyId: id },
      {
        onSuccess: () => {
          invalidateQuery();
          companyReactivateTracker();
        },
      },
    );
  }, [companyReactivateTracker, id, invalidateQuery, isReactivatePending, reactivate]);

  const handleEdit = async () => {
    await handleSelectCompany();
    navigate(PageRoute.COMPANY_SETTINGS);
  };

  return (
    <>
      <DeprecatedModalWrapper isOpen={isOpenDeactivateModal}>
        <CompanyDeactivateModal
          expDate={paymentData?.periodEnd || 0}
          onClose={handleDismissModal}
          onSuccess={handleSuccessModal}
        />
      </DeprecatedModalWrapper>

      {isCompleted || isEnterpriseRequested ? (
        <div
          className={twMerge(
            'flex h-fit w-full max-w-[400px] flex-col gap-2 rounded-lg p-[2px] pt-2 text-center shadow-sm',
            isSelected ? 'bg-forest-400 text-gray-25' : 'bg-gray-50 text-gray-300',
          )}
        >
          {isActive || isEnterpriseRequested ? (
            <span
              className={twJoin(
                'line-h cursor-pointer text-label-sm font-bold uppercase',
                !isSelected && 'cursor-pointer text-gray-700',
              )}
              onClick={() =>
                isActive ? handleSelectCompany() : handleSelectEnterpiseRequestCompany()
              }
              style={{
                fontFamily: 'Inter',
                fontSize: '10px',
                fontWeight: '700',
                lineHeight: '16px',
                letterSpacing: '1px',
                textAlign: 'center',
              }}
            >
              {isSelected ? (
                <AppFormattedMessage id="CURRENTLY_SELECTED" />
              ) : (
                <AppFormattedMessage id="SWITCH_COMPANY" />
              )}
            </span>
          ) : (
            <span
              className={twJoin('cursor-pointer text-label-sm font-bold uppercase text-gray-300')}
              style={{
                fontFamily: 'Inter',
                fontSize: '10px',
                fontWeight: '700',
                lineHeight: '16px',
                letterSpacing: '1px',
                textAlign: 'center',
              }}
            >
              <AppFormattedMessage id="INACTIVE_COMPANY" />
            </span>
          )}

          <div className="flex w-full flex-col divide-y rounded-md bg-white">
            <div className="flex w-full items-center justify-between p-4">
              <div className="flex items-center gap-2 overflow-hidden text-gray-600">
                {logoDisplaySrc ? (
                  <img
                    alt=""
                    className="size-9 shrink-0 rounded-full bg-gray-100 object-cover"
                    src={logoDisplaySrc}
                  />
                ) : (
                  <div className="size-9 shrink-0 rounded-full bg-gray-100 object-cover" />
                )}
                <span className="truncate font-[550]">{name}</span>
              </div>
              {StakeholderRole.ADMIN === roleInCompany && (
                <Popover>
                  {!isEnterpriseRequested && (
                    <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
                      <div className="flex size-6 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
                        <ThreeDotsIcon />
                      </div>
                    </PopoverTrigger>
                  )}
                  <PopoverContent className="flex w-fit flex-col rounded p-0 text-sm font-normal shadow-2xl">
                    <PopoverClose>
                      {isActive && (
                        <div
                          className="flex items-center justify-center gap-1 p-3 pr-10 text-gray-700 hover:bg-gray-50"
                          onClick={handleEdit}
                        >
                          <EditIcon />
                          <span>
                            <AppFormattedMessage id="EDIT_DETAILS" />
                          </span>
                        </div>
                      )}

                      {isActive && !toCancelAtFormattedData && !inactivateAtFormattedData && (
                        <div
                          className="flex items-center gap-1 p-3 pr-10 text-fireside-500 hover:bg-gray-50"
                          onClick={handleOpenModal}
                        >
                          <ArchiveIcon />
                          <span>
                            <AppFormattedMessage id="DEACTIVATE" />
                          </span>
                        </div>
                      )}

                      {(!isActive || toCancelAtFormattedData || inactivateAtFormattedData) && (
                        <div
                          className={twMerge(
                            'flex items-center justify-center gap-1 p-3 pr-10 text-gray-700 hover:bg-gray-50',
                            isReactivatePending && 'pointer-events-none opacity-50',
                          )}
                          onClick={handleReactivateCompany}
                        >
                          <RefreshIcon />
                          <span>
                            <AppFormattedMessage
                              id={
                                isActive &&
                                (Boolean(toCancelAtFormattedData) ||
                                  Boolean(inactivateAtFormattedData))
                                  ? 'REACTIVATE'
                                  : 'PAY_AND_ACTIVATE'
                              }
                            />
                          </span>
                        </div>
                      )}
                    </PopoverClose>
                  </PopoverContent>
                </Popover>
              )}
            </div>

            <CompanyCardItem
              title={<AppFormattedMessage id="COMPANY_TYPE" />}
              value={type ? <AppFormattedMessage id={companyTypeTranslation[type]} /> : '-'}
            />
            <CompanyCardItem
              className="py-[6px]"
              title={<AppFormattedMessage id="ACCOUNT" />}
              value={
                isEnterpriseRequested ? (
                  'Awaiting for enterprise request approval'
                ) : isActive ? (
                  toCancelAtFormattedData || inactivateAtFormattedData ? (
                    <AppFormattedMessage
                      id="ACTIVE_UNTIL"
                      values={{ date: toCancelAtFormattedData || inactivateAtFormattedData }}
                    />
                  ) : (
                    <AppFormattedMessage id="ACTIVE" />
                  )
                ) : (
                  <AppFormattedMessage id="INACTIVE" />
                )
              }
              valueClassName={twMerge(
                'px-2 py-1 rounded',
                isEnterpriseRequested
                  ? 'text-[#B54708] bg-[#FFFAEB]'
                  : isActive
                    ? toCancelAtFormattedData || inactivateAtFormattedData
                      ? 'text-[#B54708] bg-[#FFFAEB]'
                      : 'text-forest-500 bg-forest-50'
                    : 'text-gray-400 bg-gray-100',
              )}
            />
            <CompanyCardItem
              title={<AppFormattedMessage id="USER_ROLE" />}
              value={
                isExternalGuest
                  ? messagesLocale.EXTERNAL_GUEST
                  : messagesLocale[stakeholderRoleTranslation[roleInCompany]]
              }
            />
            <CompanyCardItem
              title={<AppFormattedMessage id="DATE_CREATED" />}
              value={createdAtFormattedData}
            />
          </div>
        </div>
      ) : (
        <IncompleteCompanyCard company={company} invalidateQuery={invalidateQuery} />
      )}
    </>
  );
};

export default MyCompanyCard;
